import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  useToast,
} from '@/components/ui';
import { request } from '@/lib/api';
import type { ChecklistItem } from '@/lib/schemas';
import { useEmailEnabled } from '@/lib/use-email-enabled';

/**
 * סגירת משימה היא פעולה שמפעילה שרשרת: ניכוי מלאי, שורות חיוב,
 * ומייל ללקוח. לכן היא מאחורי אישור מפורש שמראה מה עומד לקרות —
 * ולא כפתור בודד שאפשר ללחוץ עליו בטעות.
 *
 * הפעולה עצמה אידמפוטנטית בשרת (סגירה כפולה לא מנכה מלאי פעמיים),
 * אבל זה הרשת ולא התוכנית: הכפתור ננעל בזמן השליחה.
 */
export function CloseTaskDialog({
  taskId,
  checklist,
}: {
  taskId: string;
  checklist: ChecklistItem[];
}) {
  const { t } = useTranslation(['tasks', 'common']);
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const toast = useToast();
  const email = useEmailEnabled();

  const close = useMutation({
    mutationFn: () =>
      request<{ alreadyClosed?: boolean }>(`/tasks/${taskId}/close`, {
        method: 'POST',
        body: {
          checklist: checklist.map((c) => ({
            label: c.label,
            done: c.done,
            ...(c.priceCode ? { priceCode: c.priceCode } : {}),
            ...(c.sku ? { sku: c.sku } : {}),
            ...(c.qty ? { qty: c.qty } : {}),
            ...(c.priceQty ? { priceQty: c.priceQty } : {}),
          })),
        },
      }),
    onSuccess: async (result) => {
      setOpen(false);
      // השרת מבחין בין סגירה אמיתית לבין ניסיון חוזר. ההודעה
      // משקפת מה באמת קרה — "נסגרה" על משימה שכבר הייתה סגורה
      // זה שקר קטן שמבלבל בהמשך.
      if (result?.alreadyClosed) toast.info(t('close.alreadyClosed'));
      else toast.success(t('close.closed'), email.enabled ? t('close.closedHintEmail') : t('close.closedHint'));
      await qc.invalidateQueries({ queryKey: ['task', taskId] });
      await qc.invalidateQueries({ queryKey: ['tasks'] });
    },
    onError: (err: Error) => toast.error(t('close.failed'), err.message),
  });

  const done = checklist.filter((c) => c.done).length;
  const pending = checklist.length - done;
  const consumesStock = checklist.some((c) => c.done && c.sku);
  const billable = checklist.some((c) => c.done && c.priceCode);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <CheckCircle2 aria-hidden />
          {t('close.trigger')}
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('close.title')}</DialogTitle>
          <DialogDescription>{t('close.description')}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          {checklist.length > 0 ? (
            <div className="rounded-(--radius-md) border border-border bg-surface-sunken/50 p-3 text-xs">
              <p className="tabular text-fg">{t('close.checked', { done, count: checklist.length })}</p>
              {pending > 0 ? (
                <p className="mt-1 text-warning">{t('close.pending', { count: pending })}</p>
              ) : null}
            </div>
          ) : null}

          <ul className="space-y-1.5 text-xs text-fg-muted">
            <li>{t('close.willComplete')}</li>
            {consumesStock ? <li>{t('close.willDeductStock')}</li> : null}
            {billable ? <li>{t('close.willBill')}</li> : null}
            {email.enabled ? (
              <li>{t('close.willEmail')}</li>
            ) : (
              // אמירת האמת עדיפה על שתיקה: המשתמש צריך לדעת שהלקוח
              // לא יעודכן, כדי שיוכל להתקשר בעצמו.
              <li className="text-warning">
                <Trans t={t} i18nKey="close.noEmail" components={{ b: <strong /> }} />
              </li>
            )}
          </ul>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
            {t('common:actions.cancel')}
          </Button>
          <Button size="sm" loading={close.isPending} onClick={() => close.mutate()}>
            {t('close.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
